import { useState, useEffect } from "react";
import { X, Download, Smartphone, Share, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function InstallAppPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isChromeOnIOS, setIsChromeOnIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Verificăm dacă e deja instalată (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Detectăm iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Detectăm Chrome pe iOS (CriOS = Chrome iOS)
    const chromeOnIOS = iOS && /CriOS/.test(navigator.userAgent);
    setIsChromeOnIOS(chromeOnIOS);

    // Detectăm mobil
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Verificăm dacă userul a închis prompt-ul recent (24h)
    const dismissedTime = localStorage.getItem('pwa_prompt_dismissed');
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    const wasDismissedRecently = dismissedTime && parseInt(dismissedTime) > oneDayAgo;

    // Verificăm dacă a instalat deja
    const hasInstalled = localStorage.getItem('pwa_installed') === 'true';

    // Ascultăm evenimentul beforeinstallprompt (Chrome/Edge/Samsung)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Afișăm prompt-ul doar pe mobil și dacă nu a fost respins recent
      if (isMobile && !standalone && !wasDismissedRecently && !hasInstalled) {
        setTimeout(() => setShowPrompt(true), 3000); // După 3 secunde
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Pentru iOS (inclusiv Chrome pe iOS) - afișăm instrucțiuni manuale
    if (iOS && isMobile && !standalone && !wasDismissedRecently && !hasInstalled) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    // Ascultăm când app-ul e instalat
    window.addEventListener('appinstalled', () => {
      localStorage.setItem('pwa_installed', 'true');
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Chrome/Edge - folosim prompt-ul nativ
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwa_installed', 'true');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  // Nu afișăm dacă e deja instalată sau nu suntem pe mobil
  if (!showPrompt || isStandalone) return null;

  // Renderizare pentru Chrome pe iOS
  const renderChromeOnIOSInstructions = () => (
    <div className="px-6 pb-6">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <ExternalLink className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-800 mb-1">
              Deschide în Safari
            </p>
            <p className="text-sm text-amber-700">
              Chrome pe iPhone nu suportă instalarea aplicațiilor. Te rugăm să deschizi site-ul în Safari pentru a instala.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-slate-50 rounded-xl p-4">
        <p className="text-sm font-medium text-slate-700 mb-3">
          Pași:
        </p>
        <ol className="text-sm text-slate-600 space-y-2">
          <li className="flex items-start gap-2">
            <span className="font-semibold text-blue-600">1.</span>
            <span>Copiază link-ul: <span className="font-mono text-xs bg-slate-200 px-1 rounded">auto-cora.ro</span></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-blue-600">2.</span>
            <span>Deschide Safari și accesează site-ul</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-blue-600">3.</span>
            <span>Apasă <Share className="inline w-4 h-4 mx-1" /> → "Add to Home Screen"</span>
          </li>
        </ol>
      </div>
      <Button 
        onClick={handleDismiss}
        variant="outline"
        className="w-full mt-4"
      >
        Am înțeles
      </Button>
    </div>
  );

  // Renderizare pentru Safari pe iOS
  const renderSafariInstructions = () => (
    <div className="px-6 pb-6">
      <div className="bg-slate-50 rounded-xl p-4">
        <p className="text-sm font-medium text-slate-700 mb-3">
          Pentru a instala pe iPhone/iPad:
        </p>
        <ol className="text-sm text-slate-600 space-y-2">
          <li className="flex items-start gap-2">
            <span className="font-semibold text-blue-600">1.</span>
            <span>Apasă pe butonul <Share className="inline w-4 h-4 mx-1" /> din bara de jos</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-blue-600">2.</span>
            <span>Scroll în jos și selectează "Add to Home Screen"</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold text-blue-600">3.</span>
            <span>Apasă "Add" în colțul din dreapta sus</span>
          </li>
        </ol>
      </div>
      <Button 
        onClick={handleDismiss}
        variant="outline"
        className="w-full mt-4"
      >
        Am înțeles
      </Button>
    </div>
  );

  // Renderizare pentru Android (Chrome, etc.)
  const renderAndroidInstall = () => (
    <div className="px-6 pb-6 space-y-3">
      <Button 
        onClick={handleInstall}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-semibold"
      >
        <Download className="w-5 h-5 mr-2" />
        Instalează Aplicația
      </Button>
      <Button 
        onClick={handleDismiss}
        variant="ghost"
        className="w-full text-slate-500"
      >
        Mai târziu
      </Button>
    </div>
  );

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-end justify-center p-4 bg-black/50 animate-in fade-in duration-300"
      onClick={handleDismiss}
    >
      <div 
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl animate-in slide-in-from-bottom duration-500"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-6 pb-4">
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
            aria-label="Închide"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-4">
            {/* App Icon */}
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Smartphone className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">AutoCora</h3>
              <p className="text-slate-500 text-sm">Școala Auto</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          <p className="text-slate-600 leading-relaxed">
            Instalează aplicația pe telefonul tău pentru acces rapid la chestionare și lecții, chiar și offline!
          </p>
          
          {/* Benefits */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span>Acces rapid de pe ecranul principal</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span>Funcționează și fără internet</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span>Experiență ca o aplicație nativă</span>
            </div>
          </div>
        </div>

        {/* Conditional rendering based on device/browser */}
        {isChromeOnIOS ? renderChromeOnIOSInstructions() : 
         isIOS ? renderSafariInstructions() : 
         renderAndroidInstall()}
      </div>
    </div>
  );
}
